import React from 'react';
import { motion } from 'motion/react';

export default function CinderCrownCover() {
  return (
    <div className="absolute inset-0 w-full h-full overflow-hidden select-none bg-slate-950 font-sans shadow-inner">
      {/* LEFT DIAGONAL SIDE: Elara Vance & The Forge */}
      <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-[#120502] via-[#240b05] to-[#0d0402]">
        {/* Forge Glow Backlight */}
        <div className="absolute left-[-20%] top-[40%] w-[80%] h-[80%] rounded-full bg-orange-600/20 blur-[80px]" />
        
        {/* Bellowing Embers (Rising Particles) */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(10)].map((_, i) => (
            <motion.div
              key={`ember-${i}`}
              className="absolute w-1.5 h-1.5 rounded-full bg-amber-500"
              style={{
                left: `${15 + Math.random() * 30}%`,
                bottom: `${5 + Math.random() * 20}%`,
                opacity: 0.2 + Math.random() * 0.6,
                filter: 'blur(1px)',
              }}
              animate={{
                y: [-20, -250 - Math.random() * 150],
                x: [0, (Math.random() - 0.5) * 60],
                scale: [1, 1.8, 0],
                opacity: [0, 0.8, 0.8, 0],
              }}
              transition={{
                duration: 4 + Math.random() * 4,
                repeat: Infinity,
                delay: Math.random() * 4,
                ease: 'easeOut',
              }}
            />
          ))}
        </div>

        {/* Elara Vance Blacksmith Vector Silhouette & Blazing Sword */}
        <div className="absolute left-2.5 top-[15%] w-[45%] h-[70%] opacity-85 z-10 flex flex-col justify-end">
          {/* Glowing Fiery Sword Line / Spark Vector */}
          <div className="relative w-full h-full">
            {/* Blazing Golden Sword */}
            <motion.div 
              className="absolute left-[50%] bottom-[10%] w-[6px] h-[75%] origin-bottom bg-gradient-to-t from-red-500 via-amber-400 to-yellow-100 rounded-full shadow-[0_0_20px_#ea580c,0_0_40px_#f97316]"
              animate={{
                boxShadow: [
                  '0 0 15px #ea580c, 0 0 30px #f97316',
                  '0 0 25px #ffedd5, 0 0 50px #ea580c',
                  '0 0 15px #ea580c, 0 0 30px #f97316'
                ],
                scaleX: [1, 1.15, 1],
                rotate: [-26, -24, -26]
              }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              {/* Flame Trails */}
              <motion.div 
                className="absolute right-[-4px] top-[20%] w-[12px] h-[12px] rounded-full bg-amber-400"
                animate={{ y: [-10, -50], opacity: [1, 0], scale: [1, 0.4] }}
                transition={{ duration: 1, repeat: Infinity, ease: 'easeOut' }}
              />
              <motion.div 
                className="absolute left-[-2px] top-[40%] w-[8px] h-[8px] rounded-full bg-yellow-400"
                animate={{ y: [-8, -40], opacity: [1, 0], scale: [1, 0.3] }}
                transition={{ duration: 0.8, repeat: Infinity, ease: 'easeOut', delay: 0.2 }}
              />
              <motion.div 
                className="absolute right-[0px] top-[60%] w-[10px] h-[10px] rounded-full bg-red-500"
                animate={{ y: [-15, -60], opacity: [1, 0], scale: [1, 0.2] }}
                transition={{ duration: 1.2, repeat: Infinity, ease: 'easeOut', delay: 0.4 }}
              />
            </motion.div>

            {/* Braided Blacksmith Silhouette representation in clean CSS/SVG */}
            <svg 
              viewBox="0 0 100 100" 
              className="absolute left-[15%] bottom-[5%] w-[70%] h-[75%] text-[#1a0c0a] fill-current"
            >
              {/* Apron Body */}
              <path d="M 40 100 L 60 100 L 58 60 Q 56 42 50 42 Q 44 42 42 60 Z" />
              {/* Shoulders */}
              <ellipse cx="50" cy="50" rx="16" ry="8" />
              {/* Neck */}
              <rect x="47" y="38" width="6" height="8" />
              {/* Head with braided hair buns on side */}
              <circle cx="50" cy="34" r="8" />
              <circle cx="41" cy="32" r="3" /> {/* Left Braid bun */}
              <circle cx="59" cy="32" r="3" /> {/* Right Braid bun */}
              {/* Braids traveling down */}
              <path d="M 41 33 Q 39 42 41 55" stroke="#100706" strokeWidth="2" fill="none" />
              <path d="M 59 33 Q 61 42 59 55" stroke="#100706" strokeWidth="2" fill="none" />
              {/* Glowing Amber Eyes (Small details) */}
              <ellipse cx="48" cy="34" rx="0.7" ry="0.4" fill="#fcbe03" />
              <ellipse cx="52" cy="34" rx="0.7" ry="0.4" fill="#fcbe03" />
            </svg>
          </div>
        </div>
      </div>

      {/* RIGHT DIAGONAL SIDE: Shadow Domain & Spiky Gothic Castle */}
      <div 
        className="absolute inset-0 w-full h-full bg-gradient-to-bl from-[#08020a] via-[#0d091a] to-[#040105] shadow-2xl"
        style={{
          clipPath: 'polygon(100% 0, 38% 0, 100% 100%)',
          boxShadow: '-10px 0 35px rgba(0,0,0,0.8)'
        }}
      >
        {/* Separator fire/lightning line glow */}
        <div className="absolute left-[38%] top-0 bottom-0 w-[2px] bg-gradient-to-b from-yellow-400 via-amber-500 to-red-600 shadow-[0_0_15px_#f97316] z-20 origin-top transform rotate-[-31.8deg] scale-y-[1.8]" />

        {/* Cold Blue Backlight */}
        <div className="absolute right-[-10%] top-[10%] w-[70%] h-[70%] rounded-full bg-indigo-600/10 blur-[90px]" />

        {/* Dark Spiky Gothic Tower Silhouette */}
        <div className="absolute right-[5%] bottom-[8%] w-[45%] h-[80%] opacity-90 z-10 flex flex-col justify-end items-center">
          <svg 
            viewBox="0 0 100 150" 
            className="w-full h-full text-[#0a0712] fill-current"
          >
            {/* Spiky Jagged Gothic Mountain/Fortress Base */}
            <path d="M 10 150 L 90 150 L 85 110 L 78 120 L 72 80 L 68 95 L 60 50 L 53 115 L 48 30 L 42 105 L 35 70 L 28 110 L 18 90 Z" />
            {/* Giant Spiked Spire in center */}
            <path d="M 45 110 L 55 110 L 50 10 L 48 40 Z" />
            {/* Secondary spikes */}
            <path d="M 32 115 L 38 115 L 35 45 Z" />
            <path d="M 64 112 L 70 112 L 67 55 Z" />
          </svg>
        </div>

        {/* Shadow Knight in Gothic Spiked Armor */}
        <div className="absolute right-[4%] bottom-[5%] w-[42%] h-[68%] z-15">
          <div className="relative w-full h-full flex items-end">
            <svg 
              viewBox="0 0 100 100" 
              className="w-[90%] h-[90%] mx-auto text-[#07050d] fill-current"
            >
              {/* Massive Spiked Pauldrons (Shoulders) */}
              <path d="M 15 100 Q 20 55 35 55 L 40 68 Z" /> {/* Left shoulder */}
              <path d="M 85 100 Q 80 55 65 55 L 60 68 Z" /> {/* Right shoulder */}
              {/* Left Shoulder Spike */}
              <path d="M 22 62 L 10 40 L 28 58 Z" />
              {/* Right Shoulder Spike */}
              <path d="M 78 62 L 90 40 L 72 58 Z" />
              
              {/* Knight Helmet / Neck Armor */}
              <path d="M 38 72 L 62 72 L 50 100 Z" />
              <ellipse cx="50" cy="50" rx="13" ry="17" />
              
              {/* Helmet visor slit */}
              <path d="M 40 48 L 60 48 L 50 51 Z" fill="#020105" />

              {/* Icy Blue Glowing Eyes */}
              <motion.circle 
                cx="46" 
                cy="49" 
                r="1" 
                fill="#38bdf8"
                animate={{
                  opacity: [0.6, 1, 0.6],
                  scale: [1, 1.3, 1],
                  shadow: ['0 0 4px #0ea5e9', '0 0 8px #38bdf8', '0 0 4px #0ea5e9']
                }}
                transition={{ duration: 3, repeat: Infinity }}
              />
              <motion.circle 
                cx="54" 
                cy="49" 
                r="1" 
                fill="#38bdf8"
                animate={{
                  opacity: [0.6, 1, 0.6],
                  scale: [1, 1.3, 1],
                  shadow: ['0 0 4px #0ea5e9', '0 0 8px #38bdf8', '0 0 4px #0ea5e9']
                }}
                transition={{ duration: 3, repeat: Infinity, delay: 0.1 }}
              />
            </svg>
          </div>
        </div>
      </div>

      {/* OVERLAY TITLE & AUTHOR: Centered, highly readable, beautiful text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center z-30 pointer-events-none select-none">
        
        {/* Small gold prefix */}
        <span 
          className="text-amber-400 font-serif font-semibold text-[11px] sm:text-xs tracking-[0.3em] uppercase mb-1 drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]"
        >
          THE
        </span>
        
        {/* Large Textured Golden Title */}
        <h1 
          className="font-serif font-black text-2xl sm:text-3.5xl md:text-4xl text-transparent bg-clip-text bg-gradient-to-b from-yellow-100 via-amber-300 to-amber-500 tracking-wider leading-none drop-shadow-[0_4px_8px_rgba(0,0,0,1)] uppercase select-none font-bold"
          style={{
            textShadow: '0 4px 12px rgba(0, 0, 0, 0.95), 0 0 20px rgba(180, 83, 9, 0.35)',
            WebkitTextStroke: '0.5px rgba(251, 191, 36, 0.3)'
          }}
        >
          CINDER<br />CROWN
        </h1>
        
        {/* Separator Line */}
        <div className="w-12 h-[1px] bg-gradient-to-r from-transparent via-amber-400/70 to-transparent my-3 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" />
        
        {/* Author Credit */}
        <span 
          className="text-white hover:text-amber-50 text-[10px] sm:text-xs tracking-[0.2em] font-medium font-mono opacity-90 drop-shadow-[0_2px_5px_rgba(0,0,0,0.95)]"
        >
          WILLOW WISE
        </span>
      </div>

      {/* Atmospheric Vignette overlay to tie the elements together */}
      <div className="absolute inset-0 pointer-events-none bg-radial-vignette opacity-50 mix-blend-multiply z-25" />
    </div>
  );
}
